/**
 * Filters for the specialists / consultants lists: speciality, covered area and
 * "usually works on this date".
 */
import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { FilterAltOff as ClearFiltersIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGetProviderFilterOptionsQuery } from '../../store/api/providersApi';
import type { ProviderListFilters as Filters, ProviderSpeciality } from '../../types/providerProfile';

interface ProviderListFiltersProps {
  value: Filters;
  onChange: (next: Filters) => void;
}

const ProviderListFilters = ({ value, onChange }: ProviderListFiltersProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');
  const { data } = useGetProviderFilterOptionsQuery();
  const options = data?.data;

  const specialities = options?.specialities ?? [];
  const selectedSpeciality = specialities.find((s) => s.id === value.subservice_id) ?? null;
  // One "area" filter covers both cities and districts
  const places = Array.from(new Set([...(options?.cities ?? []), ...(options?.areas ?? [])]));

  const specialityLabel = (s: ProviderSpeciality) => (isAr && s.name_ar ? s.name_ar : s.name);
  const serviceLabel = (s: ProviderSpeciality) => (isAr && s.service_name_ar ? s.service_name_ar : s.service_name);

  const hasFilters = !!(value.subservice_id || value.area || value.available_on);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '1.4fr 1fr 200px auto' },
        gap: 2,
        alignItems: 'center',
        mt: 2,
      }}
    >
      <Autocomplete
        size="small"
        options={specialities}
        value={selectedSpeciality}
        onChange={(_e, speciality) => onChange({ ...value, subservice_id: speciality?.id })}
        getOptionLabel={specialityLabel}
        groupBy={serviceLabel}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => <TextField {...params} label={t('providers.filters.speciality')} />}
      />
      <Autocomplete
        size="small"
        options={places}
        value={value.area ?? null}
        onChange={(_e, area) => onChange({ ...value, area: area ?? undefined })}
        renderInput={(params) => <TextField {...params} label={t('providers.filters.area')} />}
      />
      <TextField
        size="small"
        type="date"
        label={t('providers.filters.availableOn')}
        value={value.available_on ?? ''}
        onChange={(e) => onChange({ ...value, available_on: e.target.value || undefined })}
        InputLabelProps={{ shrink: true }}
      />
      <Button
        size="small"
        startIcon={<ClearFiltersIcon />}
        onClick={() => onChange({})}
        disabled={!hasFilters}
      >
        {t('providers.filters.clear')}
      </Button>
    </Box>
  );
};

export default ProviderListFilters;
